import { trackFunnelStep, trackError } from '~/utils/monitoring';
import { parseISO, format } from 'date-fns';
import { nl } from 'date-fns/locale/nl';
import '~/components/filter-section/filter-section';
import '~/components/search-filter/search-filter';
import { Filter, FilterChangeEvent, SearchFilter } from '~/components/search-filter/search-filter';
import '~/components/rating/rating';
import '~/components/query-filter/query-filter';
import { QueryChangeEvent, QueryFilter } from '~/components/query-filter/query-filter';
import '~/components/selected-filters/selected-filters';
import { SelectedFilterRemovedEvent, SelectedFilters } from '~/components/selected-filters/selected-filters';
import '~/components/sorting-selector/sorting-selector';
import { SortingSelector } from '~/components/sorting-selector/sorting-selector';
import '~/components/price-range-filter/price-range-filter';
import { PriceRangeFilter, PriceRangeFilterEvent } from '~/components/price-range-filter/price-range-filter';
import '~/components/search-results-pagination/search-results-pagination';
import {
  PaginationLink,
  SearchResultsPagination
} from '~/components/search-results-pagination/search-results-pagination';
import '~/components/loader/loader';
import { LoadingIndicator } from '~/components/loader/loader';
import {
  Accommodation,
  AccommodationResult,
  AccommodationResultFilters,
  AccommodationSortOptions, AvailableFilter
} from '~/types/API/AccommodationResult';
import '~/components/accommodation-card/accommodation-card';
import { AccommodationFeatures } from '~/components/accommodation-card/accommodation-card';
import { DVGStateManager } from '~/assets/js/state';
import '~/components/error-notice/error-notice';
import { ErrorNotice } from '~/components/error-notice/error-notice';
import '~/components/dialog/dialog';
import { Dialog } from '~/components/dialog/dialog';

type AppliedFilters = {
  usps: Array<{ value: string; name?: string }>;
  amenities: Array<{ value: string; name?: string }>;
  priceRange: { min: number | null; max: number | null };
  rating: number | null;
  query: string;
  parks: Array<{ value: string; name?: string }>;
  occupancies: Array<{ value: string; name?: string }>;
  bedrooms: Array<{ value: string; name?: string }>;
  type: Array<{ value: string; name?: string }>;
  wellness: Array<{ value: string; name?: string }>;
  pets: Array<{ value: string; name?: string }>;
  outdoor: Array<{ value: string; name?: string }>;
  utility: Array<{ value: string; name?: string }>;
  bathroom: Array<{ value: string; name?: string }>;
  location: Array<{ value: string; name?: string }>;
  regions: Array<{ value: string; name?: string }>;
  childFriendly: Array<{ value: string; name?: string }>;
  popular: Array<{ value: string; name?: string }>;
};

type ArrayFilterKey = {
  [K in keyof AppliedFilters]: AppliedFilters[K] extends Array<any> ? K : never
}[keyof AppliedFilters];

type AppliedFilterKey = ArrayFilterKey | 'query' | 'priceRange';

class DVGSearchResultsPage {
  stateManager = new DVGStateManager();

  pageEl = document.querySelector<HTMLDivElement>('#dvg-search-results-block');
  searchListEl = document.querySelector<HTMLDivElement>('#dvg-search-results-list');
  searchFilterContainerEl = document.querySelector<HTMLDivElement>('#dvg-search-filters');
  paginationEl = document.querySelector<SearchResultsPagination>('#dvg-search-results-pagination');

  searchResultCountEl = document.querySelector<HTMLParagraphElement>('#dvg-search-results-count-label');
  regionResultsEl = document.querySelector<HTMLParagraphElement>('#dvg-search-results-location-label');
  sortingSelectorEl = document.querySelector<SortingSelector>('#dvg-sorting-selector');

  filterDetailsEl = document.querySelector<HTMLDetailsElement>('#dvg-search-filters-details');
  priceRangeFilterEl = document.querySelector<PriceRangeFilter>('#dvg-price-range-filter');
  selectedFiltersEl = document.querySelector<SelectedFilters>('#dvg-selected-filters');

  occupancyFilterEl = document.querySelector<SearchFilter>('#dvg-occupancy-filter');
  bedroomsFilterEl = document.querySelector<SearchFilter>('#dvg-bedrooms-filter');
  typeFilterEl = document.querySelector<SearchFilter>('#dvg-type-filter');
  wellnessFilterEl = document.querySelector<SearchFilter>('#dvg-wellness-filter');
  amenitiesFilterEl = document.querySelector<SearchFilter>('#dvg-amenities-filter');
  petsFilterEl = document.querySelector<SearchFilter>('#dvg-pets-filter');
  outdoorFilterEl = document.querySelector<SearchFilter>('#dvg-outdoor-filter');
  utilityFilterEl = document.querySelector<SearchFilter>('#dvg-utility-filter');
  bathroomFilterEl = document.querySelector<SearchFilter>('#dvg-bathroom-filter');
  locationFilterEl = document.querySelector<SearchFilter>('#dvg-location-filter');
  regionFilterEl = document.querySelector<SearchFilter>('#dvg-region-filter');
  parkFilterEl = document.querySelector<SearchFilter>('#dvg-park-filter');
  popularFilterEl = document.querySelector<SearchFilter>('#dvg-popular-filter');
  childFriendlyFilterEl = document.querySelector<SearchFilter>('#dvg-child-friendly-filter');

  loaderIndicatorEl = document.querySelector<LoadingIndicator>('#dvg-search-loading-indicator');

  viewLargeBtn = document.querySelector<HTMLButtonElement>('#dvg-view-large');
  viewCompactBtn = document.querySelector<HTMLButtonElement>('#dvg-view-compact');

  errorNotice = document.querySelector<ErrorNotice>('#dvg-search-error-notice');

  showPriceModalTrigger = document.querySelector<HTMLButtonElement>('#dvg-show-modal-button');
  priceModalDialog = document.querySelector<Dialog>('#dvg-price-disclaimer-modal');

  appliedFilters: AppliedFilters = {
    usps: [],
    amenities: [],
    priceRange: { min: null, max: null },
    rating: null,
    query: '',
    parks: [],
    occupancies: [],
    bedrooms: [],
    type: [],
    wellness: [],
    pets: [],
    outdoor: [],
    utility: [],
    bathroom: [],
    location: [],
    regions: [],
    childFriendly: [],
    popular: []
  }

  childFriendlyOptions: AvailableFilter[] = [
    { label: 'Kinderstoel', value: 'ChildChair', count: 0 },
    { label: 'Kinderbed', value: 'ChildBed', count: 0 },
    { label: 'Traphekje', value: 'StairFence', count: 0 },
    { label: 'Binnenzwembad', value: 'IndoorSwimmingPool', count: 0 },
    { label: 'Buitenzwembad', value: 'PrivateSwimmingPool', count: 0 },
    { label: 'Speeltuin', value: 'Playground', count: 0 },
    { label: 'Speeltuin in de buurt', value: 'NearbyPlayground', count: 0 },
  ]

  appliedSort = 'relevance';

  region = 'all';
  theme = '';

  LONG_WAIT_TIMEOUT = 5_000;
  API_TIMEOUT = 10_000;

  longWaitTimer: number | undefined = undefined;
  apiTimer: number | undefined = undefined;

  fetchAbortController: AbortController | null = null;

  constructor() {
    if (this.pageEl) {
      const regionFromAttribute = this.pageEl.getAttribute('data-region');
      if (regionFromAttribute) {
        this.region = regionFromAttribute;
      } else {
        this.getRegionsFromUrl();
      }

      const themeFromAttribute = this.pageEl.getAttribute('data-theme');
      if (themeFromAttribute) {
        this.theme = themeFromAttribute;
      }
    }


    if (this.sortingSelectorEl) {
      this.sortingSelectorEl.value = this.appliedSort;
    }

    this.fetchResults()
      .then((result) => {
        if (!result) return;

        const { filters } = result;
        this.parseQueryParams(filters);
        this.updateSelectedFilterLabels();
        this.getAvailableFiltersFromResults(filters);
      })
      .catch(error => {
        this.showErrorMessage('Er is een fout opgetreden bij het laden van de resultaten. Probeer het later opnieuw.');
        console.error('Error fetching initial results:', error);
        trackError(error, { step: 'search_page_viewed', trigger: 'initial_load' });
      });

    this.attachMatchMediaListener();
    this.attachFilterEvents();
    this.attachSortingEvent();
    this.attachViewToggleEvents();
    this.setupOtherListeners();
  }

  getRegionsFromUrl() {
    const urlParams = new URL(window.location.href).searchParams;
    const regions = urlParams.getAll('regions');
    if (regions.length > 0) {
      this.region = regions.join(',');
    }
  }

  setupOtherListeners() {
    if (this.showPriceModalTrigger) {
      this.showPriceModalTrigger.addEventListener('click', () => this.showPriceDisclaimerModal());
    }
  }

  attachMatchMediaListener() {
    if (!this.filterDetailsEl) return;
    const filterDetailsEl = this.filterDetailsEl;

    const mediaQuery = window.matchMedia('(min-width: 768px)');
    const handleMediaChange = (e: MediaQueryListEvent) => {
      filterDetailsEl.open = e.matches;
    };

    mediaQuery.addEventListener('change', handleMediaChange);

    // Initial check
    filterDetailsEl.open = mediaQuery.matches;
  }

  attachFilterEvents() {
    if (this.selectedFiltersEl) {
      document.addEventListener('dvg:filter-removed', (event: SelectedFilterRemovedEvent) => {
        const { type, value } = event.detail;

        switch (type) {
          case 'query':
            this.updateAppliedFilters('query', () => '');
            // Reset query filter UI
            const queryFilterEl = document.querySelector<QueryFilter>('#search-results__query');
            if (queryFilterEl && queryFilterEl.inputEl) {
              queryFilterEl.inputEl.value = '';
              queryFilterEl.query = '';
            }
            break;

          case 'priceRange':
            this.updateAppliedFilters('priceRange', () => ({ min: null, max: null }));
            // Reset price range filter UI
            if (this.priceRangeFilterEl) {
              this.priceRangeFilterEl.value = {
                min: this.priceRangeFilterEl.min,
                max: this.priceRangeFilterEl.max,
              };
            }
            break;

          default:
            if (type && type !== 'query' && type !== 'priceRange' && type !== 'rating') {
              const filterKey = type as ArrayFilterKey;
              this.updateAppliedFilters(filterKey, (filters) => filters.filter((item: { value: string | undefined; }) => item.value !== value));
              if (value) {
                this.uncheckFilterValue(filterKey, value);
              }
            } else {
              console.warn(`Unhandled filter removal for type: ${ type }`);
            }
            break;
        }

        this.applyFiltersAndFetch();
      });
    }

    if (this.searchFilterContainerEl) {
      this.searchFilterContainerEl.addEventListener('dvg:price-changed', this.handlePriceRangeEvent);

      this.searchFilterContainerEl.addEventListener('dvg:query-changed', this.handleQueryEvent);

      this.searchFilterContainerEl.addEventListener('dvg:filter-changed', (evt) => {
        const el = evt.target as HTMLElement;
        const targets: { filterId: string, stateKey: keyof AppliedFilters}[] = [
          {'filterId': 'dvg-occupancy-filter', 'stateKey': 'occupancies'},
          {'filterId': 'dvg-bedrooms-filter', 'stateKey': 'bedrooms'},
          {'filterId': 'dvg-type-filter', 'stateKey': 'type'},
          {'filterId': 'dvg-wellness-filter', 'stateKey': 'wellness'},
          {'filterId': 'dvg-amenities-filter', 'stateKey': 'amenities'},
          {'filterId': 'dvg-pets-filter', 'stateKey': 'pets'},
          {'filterId': 'dvg-outdoor-filter', 'stateKey': 'outdoor'},
          {'filterId': 'dvg-utility-filter', 'stateKey': 'utility'},
          {'filterId': 'dvg-bathroom-filter', 'stateKey': 'bathroom'},
          {'filterId': 'dvg-location-filter', 'stateKey': 'location'},
          {'filterId': 'dvg-region-filter', 'stateKey': 'regions'},
          {'filterId': 'dvg-park-filter', 'stateKey': 'parks'},
          {'filterId': 'dvg-child-friendly-filter', 'stateKey': 'childFriendly'},
          {'filterId': 'dvg-popular-filter', stateKey: 'popular'},
        ];

        const target = targets.find(t => t.filterId === el.id);
        if (target) {
          const filterKey = target.stateKey as ArrayFilterKey;
          this.handleCheckboxFilterEvent(evt, filterKey);
        } else {
          console.warn(`Unhandled filter change event from: ${el.id}`);
        }
      });
    }
  }

  attachSortingEvent() {
    if (this.sortingSelectorEl) {
      this.sortingSelectorEl.addEventListener('dvg:sorting-changed', (event) => {
        const newSort = event.detail.value;
        if (newSort !== this.appliedSort) {
          this.updateSortingOption(newSort);
        }
      });
    }
  }

  showPriceDisclaimerModal() {
    this.priceModalDialog?.show();
  }

  attachViewToggleEvents() {
    if (this.viewLargeBtn) {
      this.viewLargeBtn.addEventListener('click', () => this.setViewMode('large'));
    }

    if (this.viewCompactBtn) {
      this.viewCompactBtn.addEventListener('click', () => this.setViewMode('compact'));
    }
  }

  setViewMode(mode: 'large' | 'compact') {
    if (!this.searchListEl) return;

    // Update grid class
    if (mode === 'compact') {
      this.searchListEl.classList.add('dvg-search-results-list--compact');
    } else {
      this.searchListEl.classList.remove('dvg-search-results-list--compact');
    }

    // Update button active states
    this.viewLargeBtn?.classList.toggle('active', mode === 'large');
    this.viewCompactBtn?.classList.toggle('active', mode === 'compact');
  }

  uncheckFilterValue(filterKey: ArrayFilterKey, value: string) {
    const filterElementMap: Record<ArrayFilterKey, SearchFilter | null> = {
      usps: null,
      amenities: this.amenitiesFilterEl,
      parks: this.parkFilterEl,
      occupancies: this.occupancyFilterEl,
      bedrooms: this.bedroomsFilterEl,
      type: this.typeFilterEl,
      wellness: this.wellnessFilterEl,
      pets: this.petsFilterEl,
      outdoor: this.outdoorFilterEl,
      utility: this.utilityFilterEl,
      bathroom: this.bathroomFilterEl,
      location: this.locationFilterEl,
      regions: this.regionFilterEl,
      childFriendly: this.childFriendlyFilterEl,
      popular: this.popularFilterEl,
    };

    const filterEl = filterElementMap[filterKey];
    if (filterEl && filterEl.filters) {
      filterEl.filters = filterEl.filters.map(filter => ({
        ...filter,
        checked: filter.value === value ? false : filter.checked,
      }));
    }
  }

  createRegionResultsLabel() {
    if (!this.regionResultsEl) return;

    if (this.region === 'all') {
      this.regionResultsEl.textContent = 'in heel Nederland';
      return;
    }

    const regions = this.region.split(',').map(r => this.deslugify(r));

    let preposition = 'in';
    const regionsWithAlternatePrepositions = [
      'De Veluwe',
      'De Betuwe',
      'De Waddeneilanden'
    ];

    if (regionsWithAlternatePrepositions.includes(regions[0])) {
      preposition = 'op';
    }

    this.regionResultsEl.textContent = `${preposition} ${regions.join(', ')}`;
  }

  deslugify(slug: string) {
    return slug.replace(/-/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
  }

  handleQueryEvent = (event: QueryChangeEvent) => {
    const query = event.detail.query;
    console.log('Search query updated:', query);
    this.updateAppliedFilters('query', () => query);
    this.applyFiltersAndFetch();
  }

  handlePriceRangeEvent = (event: PriceRangeFilterEvent) => {
    const { value } = event.detail;
    const { min, max } = value;

    this.updateAppliedFilters('priceRange', () => ({ min, max }));
    this.applyFiltersAndFetch();
  }

  handleCheckboxFilterEvent = (event: FilterChangeEvent, filterType: ArrayFilterKey) => {
    const selected: Filter = {
      ...event.detail,
    };

    this.updateAppliedFilters(filterType, (filters: Filter[]) => {
      if (filters.some(filter => filter.value === selected.value)) {
        return filters.filter(filter => filter.value !== selected.value);
      }
      filters.push(selected);
      return filters;
    });
    this.applyFiltersAndFetch();
  }

  updateAppliedFilters(filterType: AppliedFilterKey, modifierCallback: (currentValue: any) => any) {
    this.appliedFilters[filterType] = modifierCallback(this.appliedFilters[filterType]);
    console.log('[FILTERS] Updated applied filters:', filterType, this.appliedFilters[filterType]);
  }

  applyFiltersAndFetch() {
    this.updateSelectedFilterLabels();
    this.syncSearchParamsWithAppliedFilters();

    this.fetchResults()
      .then(() => {
        this.getRegionsFromUrl();
        this.createRegionResultsLabel();
      })
      .catch(error => {
        this.showErrorMessage('Er is een fout opgetreden bij het laden van de resultaten. Probeer het later opnieuw.');
        console.error('Error fetching results after applying filters:', error);
        trackError(error, { step: 'search_page_viewed', trigger: 'filter_applied' });
      });

    console.log('[FILTERS] Applied filters:', this.appliedFilters);
  }

  updateSelectedFilterLabels() {
    if (this.selectedFiltersEl) {
      const selectedFilters: { type: AppliedFilterKey, label: string, value: string}[] = [];

      Object.keys(this.appliedFilters).forEach((key: string) => {
        const filterKey = key as AppliedFilterKey;

        if (filterKey === 'query' && this.appliedFilters.query) {
          selectedFilters.push({ type: 'query', label: `Zoekterm: "${this.appliedFilters.query}"`, value: this.appliedFilters.query });
        }

        if (filterKey === 'priceRange' && (this.appliedFilters.priceRange.min !== null || this.appliedFilters.priceRange.max !== null)) {
          selectedFilters.push({
            type: 'priceRange',
            label: `Prijs: €${this.appliedFilters.priceRange.min || '0'} - €${this.appliedFilters.priceRange.max || 'max'}`,
            value: `${this.appliedFilters.priceRange.min || '0'}-${this.appliedFilters.priceRange.max || 'max'}`,
          });
        }

        if (!Array.isArray(this.appliedFilters[filterKey]) || this.appliedFilters[filterKey].length === 0) return;

        this.appliedFilters[filterKey].forEach(filter => {
          let label;
          switch (filterKey) {
            case 'occupancies':
              label = `Max. ${ filter.value } personen`;
              break;
            case 'bedrooms':
              if (filter.value === '1') {
                label = '1 slaapkamer';
              } else {
                label = `${ filter.value } slaapkamers`;
              }
              break;
              case 'pets':
                label = filter.label;
                break;
            case 'regions':
              label = this.deslugify(filter.value);
              break;
            default:
              label = filter.name;
          }
          selectedFilters.push({ type: filterKey, label, value: filter.value });
        });
      });

      this.selectedFiltersEl.selectedFilters = selectedFilters;
      console.log('[FILTERS] Updated selected filters:', selectedFilters);
    }
  }

  syncSearchParamsWithAppliedFilters() {
    const url = new URL(window.location.href);
    Object.keys(this.appliedFilters).forEach(filterKey => {
      const key = filterKey as AppliedFilterKey;
      switch (key) {
        case 'query':
          if (this.appliedFilters.query) {
            url.searchParams.set('search', this.appliedFilters.query);
          } else if (url.searchParams.has('search')) {
            url.searchParams.delete('search');
          }
          break;

        case 'priceRange': {
          const { min, max } = this.appliedFilters.priceRange;
          if (min !== null) {
            url.searchParams.set('min_price', min.toString(10));
          } else if (url.searchParams.has('min_price')) {
            url.searchParams.delete('min_price');
          }
          if (max !== null) {
            url.searchParams.set('max_price', max.toString(10));
          } else if (url.searchParams.has('max_price')) {
            url.searchParams.delete('max_price');
          }
          break;
        }

        case 'occupancies':
          if (this.appliedFilters.occupancies && this.appliedFilters.occupancies.length > 0) {
            const occupancies: number[] = this.appliedFilters.occupancies.map(o => parseInt(o.value));
            url.searchParams.set('min_guests', Math.min(...occupancies).toString(10));
            const maxOccupancy = Math.max(...this.appliedFilters.occupancies.map(o => {
              if (o.value.includes('-max')) {
                return Infinity;
              }
              return parseInt(o.value.split('-')[1]);
            }));
            if (maxOccupancy !== Infinity) {
              url.searchParams.set('max_guests', maxOccupancy.toString(10));
            }
          } else {
            if (url.searchParams.has('min_guests')) {
              url.searchParams.delete('min_guests');
            }
            if (url.searchParams.has('max_guests')) {
              url.searchParams.delete('max_guests');
            }
          }
          break;

        default:
          if (Array.isArray(this.appliedFilters[key]) && this.appliedFilters[key].length > 0) {
            url.searchParams.set(key, this.appliedFilters[key].map(item => item.value).join(','));
          } else if (url.searchParams.has(key)) {
            url.searchParams.delete(key);
          }
          break;
      }
    });

    if (this.appliedSort && this.appliedSort !== 'relevance') {
      url.searchParams.set('sort_by', this.appliedSort);
    }

    window.history.replaceState({}, '', url.toString());
  }

  updateSortingOption(sortBy: string) {
    this.appliedSort = sortBy;

    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sortBy);
    window.history.replaceState({}, '', url.toString());

    this.fetchResults()
      .then()
      .catch(error => {
        this.showErrorMessage('Er is een fout opgetreden bij het laden van de resultaten. Probeer het later opnieuw.');
        console.error('Error fetching results after sorting update:', error);
        trackError(error, { step: 'search_page_viewed', trigger: 'sort_applied' });
      });
  }

  async fetchResults() {
    this.hideErrorMessage();
    this.clearResults();
    this.showLoadingSpinner();

    const apiUrl = this.constructApiUrl();
    console.log('Fetching results from API URL:', apiUrl);

    try {
      this.longWaitTimer = window.setTimeout(() => {
        if (this.loaderIndicatorEl) {
          this.loaderIndicatorEl.subtext = 'Sorry, het duurt iets langer dan je van ons gewend bent...';
        }
      }, this.LONG_WAIT_TIMEOUT);

      this.apiTimer = window.setTimeout(() => {
        window.clearTimeout(this.longWaitTimer);
        window.clearTimeout(this.apiTimer);
        this.fetchAbortController?.abort();
      }, this.API_TIMEOUT);

      this.fetchAbortController = new AbortController();
      const signal = this.fetchAbortController.signal;
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        signal,
      });

      this.hideLoadingSpinner();
      window.clearTimeout(this.longWaitTimer);
      window.clearTimeout(this.apiTimer);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error details:', errorData);
        throw new Error(`API request failed: ${ response.status }`);
      }

      const { results, filters, sorting_options } = await response.json() as AccommodationResult;

      this.renderResults(results.data);
      this.updateCountLabel(results.total);
      this.getAvailableFiltersFromResults(filters);
      this.getAvailableSortingOptions(sorting_options);
      this.renderPagination(results.links);

      return { results, filters, sorting_options };
    } catch (error) {
      if (this.isAbortError(error)) {
        this.showErrorMessage('Het laden van de resultaten heeft te lang geduurd. Probeer het later opnieuw.');
      } else {
        this.showErrorMessage('Er is een fout opgetreden bij het laden van de resultaten. Probeer het later opnieuw.');
      }
      this.hideLoadingSpinner();
      console.error('Error fetching search results:', error);
    }
  }

  constructApiUrl() {
    const baseUrl = `${window.DVGBookingConfig.apiBaseUrl}/zoeken`;

    const urlParams = new URLSearchParams(window.location.search);

    const party = this.stateManager.getPartyFromURL();
    const minGuests = party.adults + party.children + party.infants;
    if (minGuests > 0) {
      urlParams.set('min_guests', minGuests.toString(10));
    }

    if (party.pets) {
      urlParams.set('pets', 'PetsAllowed');
    }

    urlParams.delete('adults');
    urlParams.delete('children');
    urlParams.delete('infants');

    if (this.region !== 'all') {
      urlParams.set('regions', this.region);
    }

    if (this.theme) {
      urlParams.set('tags', this.theme);
    }

    console.log(urlParams.toString());
    return `${baseUrl}?${urlParams.toString()}`;
  }

  renderResults(results: Accommodation[]) {
    if (!this.searchListEl) return;
    const searchListEl = this.searchListEl;

    results.forEach(result => {
      const item = this.createSearchResultItem(result);
      searchListEl.appendChild(item);
    });
  }

  clearResults() {
    if (!this.searchListEl || !this.searchResultCountEl || !this.regionResultsEl) return;

    this.searchListEl.replaceChildren();
    this.searchResultCountEl.textContent = '';
    this.regionResultsEl.textContent = '';
  }

  createSearchResultItem(result: Accommodation): HTMLLIElement {
    const li = document.createElement('li');
    li.className = 'search-result-item';

    const occupancy = result.max_occupancy;

    let properties: any[] = [];
    if (result.properties) {
      properties = result.properties;
    }
    const noOfBedrooms = parseInt(properties.find(prop => prop.code === 'CountBedrooms')?.quantity || '0')

    const features: AccommodationFeatures = {
      occupancy,
      bedrooms: noOfBedrooms,
      hotTub: properties.some(prop => prop.code === 'Jacuzzi' && prop.bool_value),
      petsAllowed: properties.some(prop => prop.code === 'PetsAllowed' && prop.bool_value),
    };

    let priceLabel = 'vanafprijs';
    const { arrival, departure } = this.stateManager.getArrivalAndDepartureFromURL();
    if (arrival && departure) {
      const arrivalLabel = format(parseISO(arrival), 'dd LLL', { locale: nl });
      const departureLabel = format(parseISO(departure), 'dd LLL', { locale: nl });
      priceLabel = `${arrivalLabel} - ${departureLabel}`;
    }

    const location = [];
    if (result.city) location.push(result.city);
    if (result.region) location.push(result.region);
    if (result.country) location.push(result.country);

    const accommodationURL = this.stateManager.constructURL(`/vakantiehuisjes/${result.slug}/`);
    li.innerHTML = `
      <dvg-accommodation-card
        name="${result.name}"
        location="${location.join(', ')}"
        image-url="${result.images[0]?.url || ''}"
        price="${result.minimum_price}"
        price-label="${priceLabel}"
        slug="${accommodationURL}"
        orientation="vertical"
      ></dvg-accommodation-card>
    `;

    const card = li.querySelector('dvg-accommodation-card');
    if (card) {
      card.features = features;
    }

    return li;
  }

  showErrorMessage(message: string) {
    if (!this.errorNotice) return;

    this.errorNotice.heading = message;
    this.errorNotice.show = true;
  }

  hideErrorMessage() {
    if (!this.errorNotice) return;

    this.errorNotice.show = false;
  }

  showLoadingSpinner() {
    if (!this.loaderIndicatorEl) return;
    this.loaderIndicatorEl.show = true;
  }

  hideLoadingSpinner() {
    if (!this.loaderIndicatorEl) return;
    this.loaderIndicatorEl.show = false;
    if (this.loaderIndicatorEl.subtext) {
      this.loaderIndicatorEl.subtext = '';
    }
  }

  updateCountLabel(count: number) {
    const plurals = new Map([
      ['one', 'vakantiehuisje'],
      ['other', 'vakantiehuisjes'],
    ]);

    const pluralCategory = new Intl.PluralRules('nl', { type: 'ordinal' }).select(count);
    const pluralText = plurals.get(pluralCategory) || plurals.get('other');

    if (this.searchResultCountEl && this.regionResultsEl) {
      this.searchResultCountEl.textContent = `${count} ${pluralText} gevonden`;
      this.createRegionResultsLabel();
    }
  }

  getAvailableFiltersFromResults(filters: AccommodationResultFilters) {
    const priceRange: AppliedFilters['priceRange'] = {
      min: filters?.available.price_range.min_price ?? 0,
      max: filters?.available.price_range.max_price ?? 100,
    };

    this.setUpPriceRangeFilter(priceRange, this.appliedFilters.priceRange);

    this.setUpOccupancyFilter(
      filters?.applied.min_guests?.value,
      filters?.applied.max_guests?.value,
      filters?.available.guest_ranges || [],
    );

    this.setUpChecklistFilter(
    this.bedroomsFilterEl,
      filters?.available.bedrooms || [],
      this.appliedFilters.bedrooms
    );
    this.setUpChecklistFilter(
    this.typeFilterEl,
      filters?.available.types || [],
      this.appliedFilters.type
    );
    this.setUpChecklistFilter(
    this.wellnessFilterEl,
      filters?.available.wellness_options || [],
      this.appliedFilters.wellness
    );
    this.setUpChecklistFilter(
    this.amenitiesFilterEl,
      filters?.available.amenities || [],
      this.appliedFilters.amenities
    );
    this.setUpChecklistFilter(
    this.petsFilterEl,
      filters?.available.pet_policy_options || [],
      this.appliedFilters.pets
    );
    this.setUpChecklistFilter(
    this.outdoorFilterEl,
      filters?.available.outdoor_options || [],
      this.appliedFilters.outdoor
    );
    this.setUpChecklistFilter(
    this.utilityFilterEl,
      filters?.available.utility_options || [],
      this.appliedFilters.utility
    );
    this.setUpChecklistFilter(
    this.bathroomFilterEl,
      filters?.available.bathroom_options || [],
      this.appliedFilters.bathroom
    );
    this.setUpChecklistFilter(
    this.locationFilterEl,
      filters?.available.location_options || [],
      this.appliedFilters.location
    );
    this.setUpChecklistFilter(
    this.regionFilterEl,
      filters?.available.regions || [],
      this.appliedFilters.regions
    );
    this.setUpChecklistFilter(
      this.parkFilterEl,
      filters?.available.parks || [],
      this.appliedFilters.parks
    );

    const childFriendlyFromApi = filters?.available.child_friendly_options || [];
    const childFriendlyFilters = childFriendlyFromApi.length > 0
      ? childFriendlyFromApi
      : this.childFriendlyOptions;
    this.setUpChecklistFilter(
      this.childFriendlyFilterEl,
      childFriendlyFilters,
      this.appliedFilters.childFriendly
    );

    this.setUpChecklistFilter(
      this.popularFilterEl,
      filters?.available.popular_options || [],
      this.appliedFilters.popular
    )
  }

  getAvailableSortingOptions(sortingOptions: AccommodationSortOptions) {
    if (this.sortingSelectorEl) {
      this.sortingSelectorEl.options = [
        ...Object.values(sortingOptions),
      ];
    }
  }

  setUpChecklistFilter(filterEl: SearchFilter | null, filterData: AvailableFilter[], appliedValues: Array<{ value: string; name?: string }>) {
    if (!filterEl) return;

    const appliedButMissingFilters = appliedValues.filter(av => !filterData.some(fd => fd.value.toString() === av.value.toString()));
    if (appliedButMissingFilters.length > 0) {
      appliedButMissingFilters.forEach(missing => {
        filterData.push({
          label: missing.name || missing.value,
          value: missing.value,
          count: 0,
        });
      });
    }

    filterEl.filters = filterData.map(item => ({
      name: item.label,
      value: item.value,
      count: item.count,
      disabled: false,
      checked: appliedValues.map(v => v.value.toString()).includes(item.value.toString()),
    }));
  }

  setUpPriceRangeFilter(filterData: AppliedFilters['priceRange'], appliedRange: AppliedFilters['priceRange']) {
    if (!this.priceRangeFilterEl) return;

    this.priceRangeFilterEl.min = filterData.min;
    this.priceRangeFilterEl.max = filterData.max;

    if (appliedRange) {
      this.priceRangeFilterEl.value = {
        min: (appliedRange.min || filterData.min) ?? 0,
        max: (appliedRange.max || filterData.max) ?? 100,
      };
    }
  }

  setUpOccupancyFilter(min: string, max: string, filterData: AvailableFilter[]) {
    if (!this.occupancyFilterEl) return;

    const relevantValues = this.getSelectedOccupancyFilterValues(filterData, min, max);

    this.occupancyFilterEl.filters = filterData.map(item => ({
      name: item.label,
      value: item.value,
      count: item.count,
      disabled: false,
      checked: relevantValues.map(v => v.value.toString()).includes(item.value),
    }));
  }

  getSelectedOccupancyFilterValues(filterData: AvailableFilter[], min: string | null, max: string | null) {
    const valuesMap = filterData.map(fd => {
      const [ min, max ] = fd.value.split('-');
      return { min: parseInt(min), max: max === 'max' ? Infinity : parseInt(max), value: fd.value };
    });

    const relevantValues: { value: string}[] = [];
    if (min !== null) {
      valuesMap.forEach(v => {
        if (v.max >= parseInt(min) && (max === undefined || max === null || parseInt(max) === Infinity || v.min <= parseInt(max))) {
          relevantValues.push({ value: v.value });
        }
      });
    }
    return relevantValues;
  }

  renderPagination(links: PaginationLink[]) {
    if (!this.paginationEl) return;

    this.paginationEl.paginationLinks = links;
  }

  parseQueryParams(filters: AccommodationResultFilters) {
    const urlParams = new URLSearchParams(window.location.search);

    const query = urlParams.get('search') || '';
    const minPriceParam = urlParams.get('min_price');
    const maxPriceParam = urlParams.get('max_price');
    const minPrice = minPriceParam ? parseInt(minPriceParam) : null;
    const maxPrice = maxPriceParam ? parseInt(maxPriceParam) : null;
    const sortBy = urlParams.get('sort_by') || 'relevance';

    const occupancies = [];
    if (urlParams.get('min_guests')) {
      const minGuestsParam = urlParams.get('min_guests');
      const maxGuestsParam = urlParams.get('max_guests');

      const selectedOccupancies = this.getSelectedOccupancyFilterValues(
        filters?.available.guest_ranges || [],
        minGuestsParam,
        maxGuestsParam
      );
      occupancies.push(...selectedOccupancies);
    }

    const extractListParam = (paramName: string, filterName: string) => {
      const paramValue = urlParams.get(paramName);
      if (!paramValue) return [];
      return paramValue.split(',')
        .map(value =>
          ({ value, name: filters?.available[filterName]?.
            find(f => f.value.toString() === value.toString())?.label || value }));
    };

    let pets: AppliedFilters['pets'] = [];
    if (urlParams.get('pets')) {
      const noOfPets = urlParams.get('pets');
      let filteredPets: AvailableFilter | undefined;
      if (noOfPets === '0') {
        filteredPets = filters?.available.pet_policy_options?.find(f => f.value === 'PetsNotAllowed');
      } else {
        filteredPets = filters?.available.pet_policy_options?.find(f => f.value === 'PetsAllowed');
      }

      if (filteredPets) {
        pets.push({ value: filteredPets.value, name: filteredPets.label });
      }
    }

    // Extract childFriendly from URL
    const childFriendlyParam = urlParams.get('childFriendly');
    const childFriendly: AppliedFilters['childFriendly'] = [];
    if (childFriendlyParam) {
      childFriendlyParam.split(',').forEach(value => {
        const option = this.childFriendlyOptions.find(opt => opt.value === value);
        if (option) {
          childFriendly.push({ value: option.value, name: option.label });
        }
      });
    }

    this.appliedFilters = {
      query,
      priceRange: { min: minPrice, max: maxPrice },
      occupancies,
      bedrooms: extractListParam('bedrooms', 'bedrooms'),
      type: extractListParam('type', 'types'),
      wellness: extractListParam('wellness', 'wellness_options'),
      amenities: extractListParam('amenities', 'amenities'),
      pets,
      outdoor: extractListParam('outdoor', 'outdoor_options'),
      utility: extractListParam('utility', 'utility_options'),
      bathroom: extractListParam('bathroom', 'bathroom_options'),
      location: extractListParam('location', 'location_options'),
      parks: extractListParam('parks', 'parks'),
      regions: extractListParam('regions', 'regions'),
      childFriendly,
      popular: extractListParam('popular', 'popular'),
      usps: [],
      rating: null,
    };

    this.appliedSort = sortBy;

    console.log('Parsed query params into applied filters:', this.appliedFilters, 'and sort:', this.appliedSort);
  }

  private isAbortError(error: any): boolean {
    return error.name === 'AbortError';
  }
}

document.addEventListener('DOMContentLoaded', () => {
  trackFunnelStep('search_page_viewed');
  new DVGSearchResultsPage();
});
